import produce from "immer";
import _ from "lodash";
import { createSelectorCreator, defaultMemoize } from "reselect";
import { Identifiable } from "../types/types";

const now = new Date();
const year = now.getFullYear() - 2000;
const month = now.getMonth() + 1;
let tmpPrevNenki = "";
let tmpCurrentNenki = "";
let tmpNextNenki = "";
if (4 <= month && month <= 9) {
  tmpPrevNenki = `${year - 1}B`;
  tmpCurrentNenki = `${year}A`;
  tmpNextNenki = `${year}B`;
} else if (10 <= month && month <= 12) {
  tmpPrevNenki = `${year}A`;
  tmpCurrentNenki = `${year}B`;
  tmpNextNenki = `${year + 1}A`;
} else {
  tmpPrevNenki = `${year - 1}A`;
  tmpCurrentNenki = `${year - 1}B`;
  tmpNextNenki = `${year}A`;
}
export const nenki = {
  prev: tmpPrevNenki,
  current: tmpCurrentNenki,
  next: tmpNextNenki,
};

export const nenkiList = new Array(3).fill(0).flatMap((_, index) => {
  const year = now.getFullYear() - 2000 - 1;
  return [year + index + "A", year + index + "B"];
});

/**
 * リストが空かどうかを返す
 * @param list
 */
export const isEmptyList = <T extends any[]>(list?: T) => {
  return !list || list.length === 0;
};

/**
 * オブジェクトが空かどうかを返す
 * @param obj
 */
export const isEmptyObject = <T extends {}>(obj: T) =>
  Object.keys(obj).length === 0;

/**
 * オブジェクトのDeep copyを返す
 * @param obj
 */
export const deepCopy = <T>(obj: T) => _.cloneDeep(obj);

/**
 * プロパティ名の文字列表現を返す
 * @param name
 */
export const nameof = <T extends {}>(name: Extract<keyof T, string>): string =>
  name;

export const formatComma = (val: number) =>
  val.toString().replace(/(\d)(?=(\d{3})+$)/g, "$1,");

export const findById = <T extends Identifiable>(
  collection: T[],
  id?: number
): T | undefined => {
  return collection.find((v: T) => v.id === id);
};

export const findIndexById = <T extends Identifiable>(
  collection: T[],
  id?: number
): number => {
  return collection.findIndex((v: T) => v.id === id);
};

export const findByItem = <T extends Identifiable>(
  collection: T[],
  item: T
): T | undefined => {
  return collection.find((v: T) => v.id === item.id);
};

export const replaceItemById = <T extends Identifiable>(
  collection: T[],
  item: T
) => {
  const i = collection.findIndex((v: T) => v.id === item.id);
  if (i === -1) {
    return;
  }
  return collection.splice(i, 1, item);
};

export const replaceItemsById = <T extends Identifiable>(
  collection: T[],
  items: T[]
) => {
  return produce(collection, (draft) => {
    items.forEach((v) => {
      replaceItemById(draft as T[], v);
    });
  });
};

// const eq = (p: any, n: any) => {
//   if (p && n) {
//     console.log("@EQ", p[0], n[0]);
//   } else {
//     console.log("@EQQ", p, n);
//   }
//   console.log("IS_EQUAL", _.isEqual(p, n));
//   return _.isEqual(p, n);
// };
// export const createDeepEqualSelector = createSelectorCreator(
//   defaultMemoize,
//   eq
// );

export const createDeepEqualSelector = createSelectorCreator(
  defaultMemoize,
  _.isEqual
);

/**
 * UUID v4を生成する
 *
 * https://qiita.com/psn/items/d7ac5bdb5b5633bae165
 */
export const generateUuid = () => {
  // https://github.com/GoogleChrome/chrome-platform-analytics/blob/master/src/internal/identifier.js
  // const FORMAT: string = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx";
  const chars = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".split("");
  for (let i = 0, len = chars.length; i < len; i++) {
    switch (chars[i]) {
      case "x":
        chars[i] = Math.floor(Math.random() * 16).toString(16);
        break;
      case "y":
        chars[i] = (Math.floor(Math.random() * 4) + 8).toString(16);
        break;
    }
  }
  return chars.join("");
};

/**
 * numberのNVL
 * @param val
 * @param defaultValue
 */
export const nvlNumber = (val?: number, defaultValue?: number) =>
  val ? val : defaultValue || 0;

export const sumNumber = (...values: Array<number | undefined>) =>
  values.reduce((acc, cur) => nvlNumber(acc) + nvlNumber(cur), 0) || 0;

export const roundByPrecision = (num: number, precision: number) => {
  const digit = Math.pow(10, precision);
  const shift = Math.round(num * digit);
  return shift / digit;
};

export const calcSagyoKosu = (hikiai: number) =>
  (hikiai - hikiai * 0.33) / 70 / 10;
